import { DateTime } from 'luxon'
import { default as qs } from 'qs'
import { default as YAML } from 'yaml'
import { default as pMap } from 'p-map'
import { deepmerge } from 'deepmerge-ts'
import { default as string } from '@poppinss/string'
import { ResourcefulAggregateMethods } from '../../mixin'
import { validator as joi, encode } from '@nhtio/validation'
import { stripUndefinedValuesFromObject } from '../../utils'
import { isObject, isResourcefulModel } from '../../type_guards'
import { encode as minify } from '@nhtio/lucid-resourceful/encoding'
import { ResourcefulHttpVerb, ResourcefulRouterVerb } from '../types'
import { E_UNRESOLVABLE_MODEL } from '@nhtio/lucid-resourceful/errors'
import { isAdditionalResourcefulRouteDetails } from '../utils/type_guards'
import { getPossibleHttpErrorCodes, describeHttpError } from '../utils/http'
import { ResourcefulModelController } from '../controllers/resourceful_model_controller'
import type { OpenAPIV3 } from 'openapi-types'
import type { ResourcefulResponseFormat } from '../types'
import type { HttpContext, Request } from '@adonisjs/core/http'
import type { ResourcefulModelOpenApiSchema } from '../../types'
import type { default as ApplicationService } from '@adonisjs/core/services/app'
import type { ResourcefulRouterOptionsService } from './resourceful_router_options_service'
import type { ResourcefulRouterModelResolverService } from './resourceful_router_model_resolver_service'
import type {
  ResourcefulModel,
  ResourcefulIndexResult,
  AnySchema,
  RouteCallback,
  AdditionalResourcefulRouteDetails,
} from '@nhtio/lucid-resourceful/types'

enum HttpMethodSortOrder {
  'get' = 1,
  'post' = 2,
  'put' = 3,
  'patch' = 4,
  'delete' = 5,
  'head' = 6,
  'options' = 7,
}

const encoder = (_key: string, value: any) => {
  if (value instanceof Date) {
    return DateTime.fromJSDate(value).toISO()
  }
  if (value instanceof DateTime) {
    return value.toISO()
  }
  if (typeof value === 'bigint') {
    return value.toString()
  }
  return value
}

export class ResourcefulRouterOpenApiDocumentService {
  readonly #opts: ResourcefulRouterOptionsService
  readonly #modelMap: ResourcefulRouterModelResolverService
  readonly #controllers: Map<string, ResourcefulModelController>
  readonly #app: typeof ApplicationService
  readonly #models: Map<string, ResourcefulModel>
  readonly #modelReadSchemas: Map<ResourcefulModel, ResourcefulModelOpenApiSchema>
  readonly #modelWriteSchemas: Map<ResourcefulModel, ResourcefulModelOpenApiSchema>

  constructor(
    opts: ResourcefulRouterOptionsService,
    modelMap: ResourcefulRouterModelResolverService,
    controllers: Map<string, ResourcefulModelController>,
    app: typeof ApplicationService
  ) {
    this.#opts = opts
    this.#modelMap = modelMap
    this.#controllers = controllers
    this.#app = app
    this.#models = new Map<string, ResourcefulModel>()
    this.#modelReadSchemas = new Map<ResourcefulModel, ResourcefulModelOpenApiSchema>()
    this.#modelWriteSchemas = new Map<ResourcefulModel, ResourcefulModelOpenApiSchema>()
  }

  async boot(ctx: HttpContext) {
    const names = Object.getOwnPropertyNames(this.#modelMap)
    await pMap(
      names,
      async (name) => {
        const cached = this.#models.get(name)
        if (cached) {
          const [readable, writeable] = await Promise.all([
            cached.$asOpenApiSchemaObject(ctx, this.#app, 'read'),
            cached.$asOpenApiSchemaObject(ctx, this.#app, 'write'),
          ])
          this.#modelReadSchemas.set(cached, readable)
          this.#modelWriteSchemas.set(cached, writeable)
          return
        }
        const promiseOrError = this.#modelMap[name]
        if (promiseOrError instanceof E_UNRESOLVABLE_MODEL) return
        const resolved = await promiseOrError
        if (isResourcefulModel(resolved)) {
          this.#models.set(name, resolved)
          const [readable, writeable] = await Promise.all([
            resolved.$asOpenApiSchemaObject(ctx, this.#app, 'read'),
            resolved.$asOpenApiSchemaObject(ctx, this.#app, 'write'),
          ])
          this.#modelReadSchemas.set(resolved, readable)
          this.#modelWriteSchemas.set(resolved, writeable)
        }
      },
      {
        concurrency: 5,
      }
    )
  }

  #getBaseUrl(request: Request): string {
    const protocol = request.protocol()
    const host = request.host()
    const url = request.url()
    const normalizedBase = url.replace(/\/(openapi|swagger)?(\.(json|yaml|yml))?$/, '')
    return `${protocol}://${host}${normalizedBase}`
  }

  #makeAbsoluteUrl(ctx: HttpContext, path: string): string {
    const baseUrl = this.#getBaseUrl(ctx.request)
    while (path.startsWith('/')) {
      path = path.slice(1)
    }
    return [baseUrl, path]
      .filter((v) => 'string' === typeof v && v.trim().length > 0)
      .map((v) => v.trim())
      .join('/')
  }

  #stringifyQueryObject(obj: any): string {
    return qs.stringify(obj, { arrayFormat: 'brackets', encode: false })
  }

  #queryParamExample(obj: any): string {
    const stringified = this.#stringifyQueryObject(obj)
    return stringified.replace(/^.*=/, '')
  }

  #descriptionOfFieldWithPossibleValues(
    standaloneText: string,
    possibleValuesText: string,
    possibleValues: string[]
  ): string {
    let ret = standaloneText
    if (possibleValues.length > 0) {
      ret += `\n\n ${possibleValuesText}:\n\n - ${possibleValues.join('\n - ')}`
    }
    return ret
  }

  #getHeadersForResponseObject(
    global: {
      [key: string]: string
    },
    model?: {
      [key: string]: string
    },
    additional?: {
      [key: string]: string
    }
  ): { [key: string]: OpenAPIV3.HeaderObject } {
    const ret: { [key: string]: OpenAPIV3.HeaderObject } = {}
    const toMerge = [global, model, additional].filter(isObject)
    const all = deepmerge(...toMerge) as { [key: string]: string }
    Object.keys(all).forEach((key) => {
      ret[key] = {}
    })
    return ret
  }

  async #openapi(): Promise<OpenAPIV3.Document['openapi']> {
    return '3.0.0'
  }

  async #info(): Promise<OpenAPIV3.Document['info']> {
    return this.#opts.info
  }

  async #servers(ctx: HttpContext): Promise<OpenAPIV3.Document['servers']> {
    return [
      {
        url: this.#getBaseUrl(ctx.request),
      },
    ]
  }

  #makeMetaIndexResponseSchema(
    properties: { [key: string]: any },
    filterable: { [key: string]: any },
    sortable: string[],
    aggregatable: string[],
    format: ResourcefulResponseFormat = 'json'
  ) {
    return {
      schema: {
        type: 'object' as OpenAPIV3.NonArraySchemaObjectType,
        properties: {
          selectable: {
            description: 'Describes the fields that can be selected for the index response',
            properties: Object.assign(
              {},
              ...Object.keys(properties).map((k) => {
                const example = 'type' in properties[k] ? properties[k] : { type: 'object' }
                return {
                  [k]: {
                    type: 'object' as OpenAPIV3.NonArraySchemaObjectType,
                    properties: {
                      type: {
                        type: 'string' as OpenAPIV3.NonArraySchemaObjectType,
                        description: 'The type of the selectable field',
                        enum: ['integer', 'number', 'string', 'boolean'],
                      },
                    },
                    additionalProperties: true,
                    required: ['type'],
                    example: 'json' === format ? example : YAML.stringify(example),
                  },
                }
              })
            ),
          },
          filterable: {
            description: 'Describes the fields that can be used to filter the index response',
            type: 'object' as OpenAPIV3.NonArraySchemaObjectType,
            properties: Object.assign(
              {},
              ...Object.keys(filterable).map((k) => ({
                [k]: {
                  type: 'object' as OpenAPIV3.NonArraySchemaObjectType,
                  properties: {
                    type: {
                      type: 'string' as OpenAPIV3.NonArraySchemaObjectType,
                      description: 'The type of the filterable field',
                      enum: ['integer', 'number', 'string', 'boolean'],
                    },
                  },
                  additionalProperties: true,
                  required: ['type'],
                  example: 'json' === format ? filterable[k] : YAML.stringify(filterable[k]),
                },
              }))
            ),
          },
          sortable: {
            description: 'Describes the fields that can be used to sort the index response',
            type: 'array' as OpenAPIV3.ArraySchemaObjectType,
            items: {
              type: 'string' as OpenAPIV3.NonArraySchemaObjectType,
            },
            uniqueItems: true,
            example: 'json' === format ? sortable : YAML.stringify(sortable),
          },
          aggregatable: {
            description:
              'Describes the fields that can return aggregate calculations for the index response',
            type: 'array' as OpenAPIV3.ArraySchemaObjectType,
            items: {
              type: 'string' as OpenAPIV3.NonArraySchemaObjectType,
            },
            uniqueItems: true,
            example: 'json' === format ? aggregatable : YAML.stringify(aggregatable),
          },
        },
        required: ['selectable', 'filterable', 'sortable', 'aggregatable'],
      },
      example:
        'yaml' === format
          ? YAML.stringify({
              selectable: properties,
              filterable,
              sortable,
              aggregatable,
            })
          : {
              selectable: properties,
              filterable,
              sortable,
              aggregatable,
            },
    }
  }

  #makeMetaWriteOperationResponseSchema(
    properties: { [key: string]: any },
    schema: AnySchema,
    encoded: string,
    format: ResourcefulResponseFormat = 'json'
  ) {
    return {
      schema: {
        type: 'object' as OpenAPIV3.NonArraySchemaObjectType,
        properties: {
          schema: {
            description: 'The OpenAPI schema for the payload',
            type: 'object' as OpenAPIV3.NonArraySchemaObjectType,
            schema: {
              properties,
            },
            example:
              'yaml' === format
                ? YAML.stringify(properties, encoder)
                : JSON.stringify(properties, encoder, 2),
          },
          joiSchema: {
            description: 'The encoded Joi schema for the payload',
            type: 'string' as OpenAPIV3.NonArraySchemaObjectType,
            example: encoded,
          },
          joiSchemaDescription: {
            description: 'The description of the Joi schema for the payload',
            type: 'object' as OpenAPIV3.NonArraySchemaObjectType,
            schema: {
              additionalProperties: true,
            },
            example:
              'yaml' === format
                ? YAML.stringify(schema.describe(), encoder)
                : JSON.stringify(schema.describe(), encoder, 2),
          },
        },
      },
      example:
        'yaml' === format
          ? YAML.stringify(
              {
                schema: properties,
                joiSchema: encoded,
                joiSchemaDescription: schema.describe(),
              },
              encoder
            )
          : {
              schema: properties,
              joiSchema: encoded,
              joiSchemaDescription: schema.describe(),
            },
    }
  }

  #makeSchemaForAdditionalRoute(
    method: ResourcefulHttpVerb,
    path: string,
    callbackOrRouteDetails: RouteCallback | AdditionalResourcefulRouteDetails
  ): OpenAPIV3.OperationObject {
    const operationId = string.camelCase(`${method}_${path.replace(/^\//, '_')}`)
    if (isAdditionalResourcefulRouteDetails(callbackOrRouteDetails)) {
      const operation: OpenAPIV3.OperationObject = {
        tags: Array.isArray(callbackOrRouteDetails.tags)
          ? callbackOrRouteDetails.tags
          : 'string' === typeof callbackOrRouteDetails.tags
            ? [callbackOrRouteDetails.tags]
            : [],
        summary: callbackOrRouteDetails.title,
        description: callbackOrRouteDetails.description,
        operationId,
        parameters: callbackOrRouteDetails.parameters,
        requestBody: callbackOrRouteDetails.requestPayloadSchema
          ? {
              content: {
                'application/json': {
                  schema: callbackOrRouteDetails.requestPayloadSchema,
                },
                'application/x-www-form-urlencoded': {
                  schema: callbackOrRouteDetails.requestPayloadSchema,
                },
                'multipart/form-data': {
                  schema: callbackOrRouteDetails.requestPayloadSchema,
                },
                'application/x-yaml': {
                  schema: callbackOrRouteDetails.requestPayloadSchema,
                },
              },
            }
          : undefined,
        responses: callbackOrRouteDetails.responsePayloadSchema
          ? {
              '200': {
                description: 'Successful response',
                headers: this.#getHeadersForResponseObject(this.#opts.headers),
                content: {
                  'application/json': {
                    schema: callbackOrRouteDetails.responsePayloadSchema,
                  },
                  'application/x-yaml': {
                    schema: callbackOrRouteDetails.responsePayloadSchema,
                  },
                },
              },
            }
          : {},
      }
      return operation
    } else {
      const operation: OpenAPIV3.OperationObject = {
        tags: [this.#opts.tagMap.additional],
        summary: `Custom ${method.toUpperCase()} Route`,
        operationId,
        responses: {},
      }
      return operation
    }
  }

  async #paths(ctx: HttpContext): Promise<OpenAPIV3.Document['paths']> {
    const ret: OpenAPIV3.PathsObject = {
      '/': {
        get: {
          summary: `${this.#opts.info.title} OpenAPI Specification`,
          operationId: `resourcefulOpenApiSpecRoot`,
          tags: [this.#opts.tagMap.general],
          responses: {
            '200': {
              description: 'The document you are currently viewing',
              headers: this.#getHeadersForResponseObject(this.#opts.headers, undefined, {
                'x-api-version': this.#opts.info.version,
                'x-api-base-url': this.#getBaseUrl(ctx.request),
              }),
              content: {
                'application/json': {
                  schema: {
                    type: 'object',
                    additionalProperties: true,
                  },
                },
                'application/x-yaml': {
                  schema: {
                    type: 'object',
                    additionalProperties: true,
                  },
                },
              },
            },
          },
        },
      },
    }
    const prefixesAndModels = Array.from(this.#models.entries())
    await pMap(
      prefixesAndModels,
      async ([prefix, model]) => {
        if (!isResourcefulModel(model)) return
        const modelController = this.#controllers.get(prefix)
        if (!modelController) return
        const asResourceful = await model.$getAsResourcefulForContext(ctx, this.#app)
        const base = `/${prefix}`
        const resourcefulModelName = model.$resourcefulName
        const readable = this.#modelReadSchemas.get(model)
        const writable = this.#modelWriteSchemas.get(model)
        if (!readable || !writable) return
        const creatable = { ...writable }
        creatable.properties = stripUndefinedValuesFromObject({
          ...creatable.properties,
          id: undefined,
        }) as unknown as OpenAPIV3.SchemaObject['properties']
        creatable.required = creatable.required
          ? creatable.required.filter((r) => r !== 'id' && r in creatable.properties!)
          : []
        const modelJoiSchema =
          await ResourcefulModelController.getResourcefulModelCreatePayloadValidationSchema(
            model,
            ctx,
            this.#app
          )
        const modelCreateSchema = joi.object(
          Object.assign(
            {},
            ...Object.keys(creatable.properties!).map((creatablePropertyKey) => {
              const returnable = { [creatablePropertyKey]: joi.any() }
              const prop = asResourceful.properties[creatablePropertyKey]
              if (prop) {
                const validator = prop.validator
                if (validator) {
                  returnable[creatablePropertyKey] = validator
                }
              }
              try {
                const extracted = modelJoiSchema.extract(creatablePropertyKey)
                if (extracted) {
                  returnable[creatablePropertyKey] =
                    returnable[creatablePropertyKey].concat(extracted)
                }
              } catch {
                // noop
              }
              return returnable
            })
          )
        )
        const modelUpdateSchema = joi.object(
          Object.assign(
            {},
            ...Object.keys(writable.properties!).map((writablePropertyKey) => {
              const returnable = { [writablePropertyKey]: joi.any() }
              const prop = asResourceful.properties[writablePropertyKey]
              if (prop) {
                const validator = prop.validator
                if (validator) {
                  returnable[writablePropertyKey] = validator
                }
              }
              try {
                const extracted = modelJoiSchema.extract(writablePropertyKey)
                if (extracted) {
                  returnable[writablePropertyKey] =
                    returnable[writablePropertyKey].concat(extracted)
                }
              } catch {
                // noop
              }
              return returnable
            })
          )
        )
        const encodedModelCreateSchema = encode(modelCreateSchema)
        const encodedModelUpdateSchema = encode(modelUpdateSchema)
        const propertyNames = readable.properties ? Object.keys(readable.properties) : []
        const filterable = readable['x-resourceful-filterable'] || []
        const sortable = readable['x-resourceful-sortable'] || []
        const aggregatable = readable['x-resourceful-aggregatable'] || []

        const indexResponseFor = (
          format: 'json' | 'yaml',
          properties: OpenAPIV3.SchemaObject['properties']
        ): OpenAPIV3.SchemaObject => {
          const exampleObject: ResourcefulIndexResult = {
            records: [
              { id: 1 },
              { id: 2 },
              { id: 3 },
              { id: 4 },
              { id: 5 },
              { id: 6 },
              { id: 7 },
              { id: 8 },
              { id: 9 },
              { id: 10 },
            ],
            total: 100,
            page: 1,
            perPage: 10,
            aggregations:
              aggregatable.length === 0
                ? {}
                : {
                    [aggregatable[0]]: {
                      avg: 50,
                      min: 1,
                      max: 100,
                      sum: 500,
                      countDistinct: 10,
                      sumDistinct: 500,
                      avgDistinct: 50,
                    },
                  },
            countQuery: '',
            recordsQuery: '',
          }
          return stripUndefinedValuesFromObject({
            schema: {
              type: 'object',
              properties: {
                records: {
                  type: 'array',
                  items: {
                    type: 'object',
                    properties,
                  },
                },
                total: {
                  type: 'integer',
                  description: 'The total number of items matching the query',
                },
                page: {
                  type: 'integer',
                  description: 'The current page number',
                },
                perPage: {
                  type: 'integer',
                  description: 'The number of items per page',
                },
                aggregations: {
                  type: 'object',
                  properties: Object.assign(
                    {},
                    ...aggregatable.map((f) => ({
                      [f]: {
                        type: 'object',
                        properties: {
                          avg: {
                            type: 'number',
                          },
                          min: {
                            type: 'number',
                          },
                          max: {
                            type: 'number',
                          },
                          sum: {
                            type: 'number',
                          },
                          countDistinct: {
                            type: 'number',
                          },
                          sumDistinct: {
                            type: 'number',
                          },
                          avgDistinct: {
                            type: 'number',
                          },
                        },
                      },
                    }))
                  ),
                },
              },
            },
            example:
              'json' === format
                ? JSON.stringify(
                    ResourcefulModelController.resourcefulIndexResultToConsumable(exampleObject),
                    null,
                    2
                  )
                : 'yaml' === format
                  ? YAML.stringify(
                      ResourcefulModelController.resourcefulIndexResultToConsumable(exampleObject)
                    )
                  : undefined,
          })
        }

        ret[base] = {
          get: {
            summary: `Filterable Index of ${resourcefulModelName} Model`,
            tags: [`${resourcefulModelName} Model`],
            operationId: string.camelCase(['serve', resourcefulModelName, 'index'].join('_')),
            parameters: [
              {
                name: 'filter',
                in: 'query',
                allowReserved: true,
                description: this.#descriptionOfFieldWithPossibleValues(
                  'The lucene filter to apply to the results',
                  'Filterable fields',
                  Object.keys(filterable)
                ),
                required: false,
                allowEmptyValue: true,
                schema: {
                  type: 'string',
                  example: 'id:[20 TO 30]',
                },
                example: this.#queryParamExample({
                  filter: 'id:[20 TO 30]',
                }),
              },
              {
                name: 'page',
                in: 'query',
                description: 'The page number to return',
                required: false,
                schema: {
                  type: 'integer',
                  minimum: 1,
                  example: 1,
                },
                example: this.#queryParamExample({
                  page: 1,
                }),
              },
              {
                name: 'perPage',
                in: 'query',
                description: 'The number of items to return per page',
                required: false,
                schema: {
                  type: 'integer',
                  minimum: 1,
                  maximum: 100,
                  example: 20,
                },
                example: this.#queryParamExample({
                  perPage: 20,
                }),
              },
              {
                name: 'fields',
                in: 'query',
                description: this.#descriptionOfFieldWithPossibleValues(
                  'A comma separated list of the fields to include in the response',
                  'Available fields',
                  propertyNames
                ),
                required: false,
                allowEmptyValue: true,
                style: 'form',
                explode: false,
                schema: {
                  type: 'array',
                  items: {
                    type: 'string',
                    enum: propertyNames,
                  },
                },
                example: this.#queryParamExample({
                  fields: propertyNames.length > 0 ? propertyNames.slice(1, 3).join(',') : 'id',
                }),
              },
              {
                name: 'sort',
                in: 'query',
                description:
                  'The sort order to return results in. Sort operations will be applied in the order that they are specified.',
                required: false,
                allowEmptyValue: true,
                explode: true,
                style: 'deepObject',
                schema: {
                  type: 'object',
                  properties: Object.assign(
                    {},
                    ...sortable.map((field) => ({
                      [field]: {
                        type: 'string',
                        enum: ['asc', 'desc'],
                        default: 'asc',
                        example: 'asc',
                      },
                    }))
                  ),
                },
                example: this.#stringifyQueryObject({
                  sort:
                    sortable.length > 0
                      ? Object.assign(
                          {},
                          ...sortable.slice(0, 2).map((field) => ({ [field]: 'desc' }))
                        )
                      : { id: 'desc' },
                }),
              },
              aggregatable.length > 0
                ? {
                    name: 'aggregations',
                    in: 'query',
                    description: this.#descriptionOfFieldWithPossibleValues(
                      'An object containing a comma separated list of the aggregations to return with the results, per field',
                      'Aggregatable fields',
                      aggregatable
                    ),
                    required: false,
                    allowEmptyValue: true,
                    explode: true,
                    style: 'deepObject',
                    schema: {
                      type: 'object',
                      properties: Object.assign(
                        {},
                        ...aggregatable.map((field) => ({
                          [field]: {
                            type: 'array',
                            items: {
                              type: 'string',
                              enum: ResourcefulAggregateMethods,
                              description: this.#descriptionOfFieldWithPossibleValues(
                                `The aggregation methods to apply to the ${field} field`,
                                'Aggregation methods',
                                Array.from(ResourcefulAggregateMethods)
                              ),
                            },
                          },
                        }))
                      ),
                    },
                    example: this.#stringifyQueryObject({
                      aggregations: Object.assign(
                        {},
                        ...aggregatable.slice(0, 2).map((field, index) => ({
                          [field]: ResourcefulAggregateMethods.slice(0, index + 1).join(','),
                        }))
                      ),
                    }),
                  }
                : undefined,
            ].filter(Boolean) as OpenAPIV3.ParameterObject[],
            responses: {
              '200': {
                description: 'A paginated list of model instances matching the requested query',
                headers: this.#getHeadersForResponseObject(
                  this.#opts.headers,
                  modelController.options.headers,
                  {
                    'x-metadata-url': this.#makeAbsoluteUrl(ctx, `${base}/$meta.index`),
                    'x-shortcut-url': this.#makeAbsoluteUrl(
                      ctx,
                      [
                        base,
                        's',
                        minify(
                          stripUndefinedValuesFromObject({
                            filter: 'id:[20 TO 30]',
                            page: 1,
                            perPage: 20,
                            fields:
                              propertyNames.length > 0 ? propertyNames.slice(1, 3).join(',') : 'id',
                            sort:
                              sortable.length > 0
                                ? Object.assign(
                                    {},
                                    ...sortable.slice(0, 2).map((field) => ({ [field]: 'desc' }))
                                  )
                                : { id: 'desc' },
                            aggregations: aggregatable.length
                              ? Object.assign(
                                  {},
                                  ...aggregatable.slice(0, 2).map((field, index) => ({
                                    [field]: ResourcefulAggregateMethods.slice(0, index + 1).join(
                                      ','
                                    ),
                                  }))
                                )
                              : undefined,
                          })
                        ),
                      ].join('/')
                    ),
                  }
                ),
                content: {
                  'application/json': indexResponseFor('json', readable.properties),
                  'application/x-yaml': indexResponseFor('yaml', readable.properties),
                },
              },
            },
          },
          post: {
            summary: `Create a new ${resourcefulModelName} Model instance`,
            tags: [`${resourcefulModelName} Model`],
            operationId: string.camelCase(['serve', resourcefulModelName, 'create'].join('_')),
            requestBody: {
              content: {
                'application/json': {
                  schema: creatable,
                },
                'application/x-www-form-urlencoded': {
                  schema: creatable,
                },
                'multipart/form-data': {
                  schema: creatable,
                },
                'application/x-yaml': {
                  schema: creatable,
                },
              },
            },
            responses: {
              '201': {
                description: `The newly created ${resourcefulModelName} Model instance`,
                headers: this.#getHeadersForResponseObject(
                  this.#opts.headers,
                  modelController.options.headers,
                  {
                    'x-metadata-url': this.#makeAbsoluteUrl(ctx, `${base}/$meta.create`),
                  }
                ),
                content: {
                  'application/json': {
                    schema: readable,
                  },
                  'application/x-yaml': {
                    schema: readable,
                  },
                },
              },
            },
          },
          put: {
            summary: `Update an existing ${resourcefulModelName} Model instance with ID in payload`,
            tags: [`${resourcefulModelName} Model`],
            operationId: string.camelCase(
              ['serve', resourcefulModelName, 'putUpdateWithIdInPayload'].join('_')
            ),
            requestBody: {
              content: {
                'application/json': {
                  schema: stripUndefinedValuesFromObject({
                    ...writable,
                    required: undefined,
                  }),
                },
                'application/x-www-form-urlencoded': {
                  schema: stripUndefinedValuesFromObject({
                    ...writable,
                    required: undefined,
                  }),
                },
                'multipart/form-data': {
                  schema: stripUndefinedValuesFromObject({
                    ...writable,
                    required: undefined,
                  }),
                },
                'application/x-yaml': {
                  schema: stripUndefinedValuesFromObject({
                    ...writable,
                    required: undefined,
                  }),
                },
              },
            },
            responses: {
              '201': {
                description: `The updated ${resourcefulModelName} Model instance`,
                headers: this.#getHeadersForResponseObject(
                  this.#opts.headers,
                  modelController.options.headers,
                  {
                    'x-metadata-url': this.#makeAbsoluteUrl(ctx, `${base}/$meta.update`),
                  }
                ),
                content: {
                  'application/json': {
                    schema: readable,
                  },
                  'application/x-yaml': {
                    schema: readable,
                  },
                },
              },
            },
          },
        }

        const metaIndexResponseSchema = this.#makeMetaIndexResponseSchema(
          readable.properties as any,
          filterable,
          sortable,
          aggregatable
        )

        const metaCreateOperationResponseSchema = this.#makeMetaWriteOperationResponseSchema(
          writable.properties as any,
          modelCreateSchema,
          encodedModelCreateSchema
        )

        const metaUpdateOperationResponseSchema = this.#makeMetaWriteOperationResponseSchema(
          writable.properties as any,
          modelUpdateSchema,
          encodedModelUpdateSchema
        )

        const metaBulkUpdateOperationResponseSchema = {
          schema: {
            type: 'object' as OpenAPIV3.NonArraySchemaObjectType,
            properties: deepmerge(
              stripUndefinedValuesFromObject({
                filterable: {
                  ...metaIndexResponseSchema.schema.properties!.filterable,
                  description: this.#descriptionOfFieldWithPossibleValues(
                    'The lucene filter used to select the records to update',
                    'Filterable fields',
                    Object.keys(filterable)
                  ),
                },
              }),
              metaUpdateOperationResponseSchema.schema.properties!
            ),
          },
        }

        ret[`${base}/$meta.index`] = {
          get: {
            summary: `${resourcefulModelName} index operation metadata`,
            tags: [`${resourcefulModelName} Model`],
            operationId: string.camelCase(
              ['serve', resourcefulModelName, 'meta', 'for', 'index'].join('_')
            ),
            responses: {
              '200': {
                description: `The metadata for the ${resourcefulModelName} index operation`,
                headers: this.#getHeadersForResponseObject(
                  this.#opts.headers,
                  modelController.options.headers
                ),
                content: {
                  'application/json': metaIndexResponseSchema,
                  'application/x-yaml': metaIndexResponseSchema,
                },
              },
            },
          },
        }
        ret[`${base}/$meta.create`] = {
          get: {
            summary: `${resourcefulModelName} create operation metadata`,
            tags: [`${resourcefulModelName} Model`],
            operationId: string.camelCase(
              ['serve', resourcefulModelName, 'meta', 'for', 'create'].join('_')
            ),
            responses: {
              '200': {
                description: `The metadata for the ${resourcefulModelName} create operation`,
                headers: this.#getHeadersForResponseObject(
                  this.#opts.headers,
                  modelController.options.headers
                ),
                content: {
                  'application/json': metaCreateOperationResponseSchema,
                  'application/x-yaml': metaCreateOperationResponseSchema,
                },
              },
            },
          },
        }
        ret[`${base}/$meta.update`] = {
          get: {
            summary: `${resourcefulModelName} update operation metadata`,
            tags: [`${resourcefulModelName} Model`],
            operationId: string.camelCase(
              ['serve', resourcefulModelName, 'meta', 'for', 'update'].join('_')
            ),
            responses: {
              '200': {
                description: `The metadata for the ${resourcefulModelName} update operation`,
                headers: this.#getHeadersForResponseObject(
                  this.#opts.headers,
                  modelController.options.headers
                ),
                content: {
                  'application/json': metaUpdateOperationResponseSchema,
                  'application/x-yaml': metaUpdateOperationResponseSchema,
                },
              },
            },
          },
        }
        ret[`${base}/$meta.bulk.update`] = {
          get: {
            summary: `${resourcefulModelName} bulk.update operation metadata`,
            tags: [`${resourcefulModelName} Model`],
            operationId: string.camelCase(
              ['serve', resourcefulModelName, 'meta', 'for', 'bulkUpdate'].join('_')
            ),
            responses: {
              '200': {
                description: `The metadata for the ${resourcefulModelName} bulk.update operation`,
                headers: this.#getHeadersForResponseObject(
                  this.#opts.headers,
                  modelController.options.headers
                ),
                content: {
                  'application/json': metaBulkUpdateOperationResponseSchema,
                  'application/x-yaml': metaBulkUpdateOperationResponseSchema,
                },
              },
            },
          },
        }

        const bulkUpdateResponses = {
          '201': {
            description: `The updated ${resourcefulModelName} Model instances`,
            headers: this.#getHeadersForResponseObject(
              this.#opts.headers,
              modelController.options.headers,
              {
                'x-metadata-url': this.#makeAbsoluteUrl(ctx, `${base}/$meta.bulk.update`),
              }
            ),
            content: {
              'application/json': {
                schema: {
                  type: 'array' as OpenAPIV3.ArraySchemaObjectType,
                  items: {
                    ...readable,
                    type: 'object' as OpenAPIV3.NonArraySchemaObjectType,
                  },
                },
              },
              'application/x-yaml': {
                schema: {
                  type: 'array' as OpenAPIV3.ArraySchemaObjectType,
                  items: {
                    ...readable,
                    type: 'object' as OpenAPIV3.NonArraySchemaObjectType,
                  },
                },
              },
            },
          },
        }

        ret[`${base}/$bulk`] = {
          put: {
            summary: `Bulk update existing ${resourcefulModelName} Model instances by filter`,
            tags: [`${resourcefulModelName} Model`],
            operationId: string.camelCase(
              ['serve', resourcefulModelName, 'putBulkUpdateWithFilter'].join('_')
            ),
            parameters: [
              {
                name: 'filter',
                in: 'query',
                allowReserved: true,
                description: this.#descriptionOfFieldWithPossibleValues(
                  'The lucene filter to apply to the results',
                  'Filterable fields',
                  Object.keys(filterable)
                ),
                required: false,
                allowEmptyValue: true,
                schema: {
                  type: 'string',
                  example: 'id:[20 TO 30]',
                },
                example: this.#queryParamExample({
                  filter: 'id:[20 TO 30]',
                }),
              },
            ],
            requestBody: {
              content: {
                'application/json': {
                  schema: creatable,
                },
                'application/x-www-form-urlencoded': {
                  schema: creatable,
                },
                'multipart/form-data': {
                  schema: creatable,
                },
                'application/x-yaml': {
                  schema: creatable,
                },
              },
            },
            responses: bulkUpdateResponses,
          },
          patch: {
            summary: `Bulk update existing ${resourcefulModelName} Model instances by filter`,
            tags: [`${resourcefulModelName} Model`],
            operationId: string.camelCase(
              ['serve', resourcefulModelName, 'patchBulkUpdateWithFilter'].join('_')
            ),
            parameters: [
              {
                name: 'filter',
                in: 'query',
                allowReserved: true,
                description: this.#descriptionOfFieldWithPossibleValues(
                  'The lucene filter to apply to the results',
                  'Filterable fields',
                  Object.keys(filterable)
                ),
                required: false,
                allowEmptyValue: true,
                schema: {
                  type: 'string',
                  example: 'id:[20 TO 30]',
                },
                example: this.#queryParamExample({
                  filter: 'id:[20 TO 30]',
                }),
              },
            ],
            requestBody: {
              content: {
                'application/json': {
                  schema: creatable,
                },
                'application/x-www-form-urlencoded': {
                  schema: creatable,
                },
                'multipart/form-data': {
                  schema: creatable,
                },
                'application/x-yaml': {
                  schema: creatable,
                },
              },
            },
            responses: bulkUpdateResponses,
          },
        }
        ret[`${base}/$bulk/{uids}`] = {
          put: {
            summary: `Bulk update existing ${resourcefulModelName} Model instances by unique ids`,
            tags: [`${resourcefulModelName} Model`],
            operationId: string.camelCase(
              ['serve', resourcefulModelName, 'putBulkUpdateWithUniqueIds'].join('_')
            ),
            parameters: [
              {
                name: 'uids',
                in: 'path',
                description: `A comma separated list of unique identifier of the ${resourcefulModelName} Model instance to update`,
                required: true,
                schema: {
                  type: 'string',
                  example: '1',
                },
              },
            ],
            requestBody: {
              content: {
                'application/json': {
                  schema: creatable,
                },
                'application/x-www-form-urlencoded': {
                  schema: creatable,
                },
                'multipart/form-data': {
                  schema: creatable,
                },
                'application/x-yaml': {
                  schema: creatable,
                },
              },
            },
            responses: bulkUpdateResponses,
          },
          patch: {
            summary: `Bulk update existing ${resourcefulModelName} Model instances by unique ids`,
            tags: [`${resourcefulModelName} Model`],
            operationId: string.camelCase(
              ['serve', resourcefulModelName, 'patchBulkUpdateWithUniqueIds'].join('_')
            ),
            parameters: [
              {
                name: 'uids',
                in: 'path',
                description: `A comma separated list of unique identifier of the ${resourcefulModelName} Model instance to update`,
                required: true,
                schema: {
                  type: 'string',
                  example: '1,2,3,4,5',
                },
              },
            ],
            requestBody: {
              content: {
                'application/json': {
                  schema: creatable,
                },
                'application/x-www-form-urlencoded': {
                  schema: creatable,
                },
                'multipart/form-data': {
                  schema: creatable,
                },
                'application/x-yaml': {
                  schema: creatable,
                },
              },
            },
            responses: bulkUpdateResponses,
          },
        }

        ret[`${base}/s/{encoded}`] = {
          get: {
            summary: `Filterable Index Shortcut of ${resourcefulModelName} Model`,
            tags: [`${resourcefulModelName} Model`],
            operationId: string.camelCase(
              ['serve', resourcefulModelName, 'index', 'shortcut'].join('_')
            ),
            parameters: [
              {
                name: 'encoded',
                in: 'path',
                description: `A URL encoded string representing the filter, page, perPage, fields, sort and aggregations parameters, generated via the \`encode\` method provided by the \`@nhtio/lucid-resourceful/encoding\` package`,
                required: true,
                schema: {
                  type: 'string',
                  example: minify(
                    stripUndefinedValuesFromObject({
                      filter: 'id:[20 TO 30]',
                      page: 1,
                      perPage: 20,
                      fields: propertyNames.length > 0 ? propertyNames.slice(1, 3).join(',') : 'id',
                      sort:
                        sortable.length > 0
                          ? Object.assign(
                              {},
                              ...sortable.slice(0, 2).map((field) => ({ [field]: 'desc' }))
                            )
                          : { id: 'desc' },
                      aggregations: aggregatable.length
                        ? Object.assign(
                            {},
                            ...aggregatable.slice(0, 2).map((field, index) => ({
                              [field]: ResourcefulAggregateMethods.slice(0, index + 1).join(','),
                            }))
                          )
                        : undefined,
                    })
                  ),
                },
              },
            ],
            responses: {
              '200': {
                description: 'A paginated list of model instances matching the requested query',
                headers: this.#getHeadersForResponseObject(
                  this.#opts.headers,
                  modelController.options.headers,
                  {
                    'x-metadata-url': this.#makeAbsoluteUrl(ctx, `${base}/$meta.index`),
                    'x-full-url': this.#makeAbsoluteUrl(
                      ctx,
                      [
                        base,
                        's',
                        qs.stringify(
                          stripUndefinedValuesFromObject({
                            filter: 'id:[20 TO 30]',
                            page: 1,
                            perPage: 20,
                            fields:
                              propertyNames.length > 0 ? propertyNames.slice(1, 3).join(',') : 'id',
                            sort:
                              sortable.length > 0
                                ? Object.assign(
                                    {},
                                    ...sortable.slice(0, 2).map((field) => ({ [field]: 'desc' }))
                                  )
                                : { id: 'desc' },
                            aggregations: aggregatable.length
                              ? Object.assign(
                                  {},
                                  ...aggregatable.slice(0, 2).map((field, index) => ({
                                    [field]: ResourcefulAggregateMethods.slice(0, index + 1).join(
                                      ','
                                    ),
                                  }))
                                )
                              : undefined,
                          }),
                          { addQueryPrefix: true }
                        ),
                      ].join('/')
                    ),
                  }
                ),
                content: {
                  'application/json': indexResponseFor('json', readable.properties),
                  'application/x-yaml': indexResponseFor('yaml', readable.properties),
                },
              },
            },
          },
        }

        ret[`${base}/{uid}`] = {
          get: {
            summary: `Retrieve a specific ${resourcefulModelName} Model instance by its ID`,
            tags: [`${resourcefulModelName} Model`],
            operationId: string.camelCase(['serve', resourcefulModelName, 'read'].join('_')),
            parameters: [
              {
                name: 'uid',
                in: 'path',
                description: `The unique identifier of the ${resourcefulModelName} Model instance to retrieve`,
                required: true,
                schema: {
                  type: 'string',
                  example: '1',
                },
              },
            ],
            responses: {
              '200': {
                description: `The requested ${resourcefulModelName} Model instance`,
                headers: this.#getHeadersForResponseObject(
                  this.#opts.headers,
                  modelController.options.headers
                ),
                content: {
                  'application/json': {
                    schema: readable,
                  },
                  'application/x-yaml': {
                    schema: readable,
                  },
                },
              },
            },
          },
          put: {
            summary: `Update a specific ${resourcefulModelName} Model instance by its ID`,
            tags: [`${resourcefulModelName} Model`],
            operationId: string.camelCase(
              ['serve', resourcefulModelName, 'put', 'update'].join('_')
            ),
            parameters: [
              {
                name: 'uid',
                in: 'path',
                description: `The unique identifier of the ${resourcefulModelName} Model instance to update`,
                required: true,
                schema: {
                  type: 'string',
                  example: '1',
                },
              },
            ],
            requestBody: {
              content: {
                'application/json': {
                  schema: stripUndefinedValuesFromObject({
                    ...creatable,
                    required: undefined,
                  }),
                },
                'application/x-www-form-urlencoded': {
                  schema: stripUndefinedValuesFromObject({
                    ...creatable,
                    required: undefined,
                  }),
                },
                'multipart/form-data': {
                  schema: stripUndefinedValuesFromObject({
                    ...creatable,
                    required: undefined,
                  }),
                },
                'application/x-yaml': {
                  schema: stripUndefinedValuesFromObject({
                    ...creatable,
                    required: undefined,
                  }),
                },
              },
            },
            responses: {
              '201': {
                description: `The updated ${resourcefulModelName} Model instance`,
                headers: this.#getHeadersForResponseObject(
                  this.#opts.headers,
                  modelController.options.headers,
                  {
                    'x-metadata-url': this.#makeAbsoluteUrl(ctx, `${base}/$meta.update`),
                  }
                ),
                content: {
                  'application/json': {
                    schema: readable,
                  },
                  'application/x-yaml': {
                    schema: readable,
                  },
                },
              },
            },
          },
          patch: {
            summary: `Partially update a specific ${resourcefulModelName} Model instance by its ID`,
            tags: [`${resourcefulModelName} Model`],
            operationId: string.camelCase(
              ['serve', resourcefulModelName, 'patch', 'update'].join('_')
            ),
            parameters: [
              {
                name: 'uid',
                in: 'path',
                description: `The unique identifier of the ${resourcefulModelName} Model instance to update`,
                required: true,
                schema: {
                  type: 'string',
                  example: '1',
                },
              },
            ],
            requestBody: {
              content: {
                'application/json': {
                  schema: stripUndefinedValuesFromObject({
                    ...creatable,
                    required: undefined,
                  }),
                },
                'application/x-www-form-urlencoded': {
                  schema: stripUndefinedValuesFromObject({
                    ...creatable,
                    required: undefined,
                  }),
                },
                'multipart/form-data': {
                  schema: stripUndefinedValuesFromObject({
                    ...creatable,
                    required: undefined,
                  }),
                },
                'application/x-yaml': {
                  schema: stripUndefinedValuesFromObject({
                    ...creatable,
                    required: undefined,
                  }),
                },
              },
            },
            responses: {
              '201': {
                description: `The updated ${resourcefulModelName} Model instance`,
                headers: this.#getHeadersForResponseObject(
                  this.#opts.headers,
                  modelController.options.headers,
                  {
                    'x-metadata-url': this.#makeAbsoluteUrl(ctx, `${base}/$meta.update`),
                  }
                ),
                content: {
                  'application/json': {
                    schema: readable,
                  },
                  'application/x-yaml': {
                    schema: readable,
                  },
                },
              },
            },
          },
          delete: {
            summary: `Delete a specific ${resourcefulModelName} Model instance by its ID`,
            tags: [`${resourcefulModelName} Model`],
            operationId: string.camelCase(
              ['serve', resourcefulModelName, 'delete', 'remove'].join('_')
            ),
            parameters: [
              {
                name: 'uid',
                in: 'path',
                description: `The unique identifier of the ${resourcefulModelName} Model instance to delete`,
                required: true,
                schema: {
                  type: 'string',
                  example: '1',
                },
              },
            ],
            responses: {
              '204': {
                description: `The ${resourcefulModelName} Model instance was successfully deleted`,
                headers: this.#getHeadersForResponseObject(
                  this.#opts.headers,
                  modelController.options.headers
                ),
              },
            },
          },
        }
        const relationshipPaths: Set<string> = new Set()
        await pMap(
          Array.from(model.$resourcefulRelationships.entries()),
          async ([name, relationship]) => {
            const lucidRelationshipDefinition = model.$getRelation(name)
            if (!lucidRelationshipDefinition) return
            switch (lucidRelationshipDefinition.type) {
              case 'hasOne':
              case 'belongsTo':
                return
            }
            const relatedResourcefulModel = relationship.relatedModel()
            if (!isResourcefulModel(relatedResourcefulModel)) return
            let relatedReadable = this.#modelReadSchemas.get(relatedResourcefulModel)
            if (!relatedReadable) {
              relatedReadable = await relatedResourcefulModel.$asOpenApiSchemaObject(
                ctx,
                this.#app,
                'read'
              )
              this.#modelReadSchemas.set(relatedResourcefulModel, relatedReadable)
            }
            if (!relatedReadable) return
            const relatedResourcefulModelName = relatedResourcefulModel.$resourcefulName
            const relatedPropertyNames = relatedReadable.properties
              ? Object.keys(relatedReadable.properties)
              : []
            const relatedFilterable = relatedReadable['x-resourceful-filterable'] || []
            const relatedSortable = relatedReadable['x-resourceful-sortable'] || []
            const relatedAggregatable = relatedReadable['x-resourceful-aggregatable'] || []
            const baseWithRelationship = `${base}/{uid}/${name}`
            relationshipPaths.add(baseWithRelationship)
            ret[baseWithRelationship] = {
              get: {
                summary: `Retrieve the related ${name} ${relatedResourcefulModelName} Model instances for a specific ${resourcefulModelName} Model instance by its ID`,
                tags: [`${resourcefulModelName} Model`],
                operationId: string.camelCase(
                  ['serve', resourcefulModelName, 'read', 'related', name].join('_')
                ),
                parameters: [
                  {
                    name: 'uid',
                    in: 'path',
                    description: `The unique identifier of the ${resourcefulModelName} Model instance to retrieve related instances for`,
                    required: true,
                    schema: {
                      type: 'string',
                      example: '1',
                    },
                  },
                  {
                    name: 'filter',
                    in: 'query',
                    description: this.#descriptionOfFieldWithPossibleValues(
                      'The lucene filter to apply to the results',
                      'Filterable fields',
                      Object.keys(relatedFilterable)
                    ),
                    required: false,
                    allowEmptyValue: true,
                    schema: {
                      type: 'string',
                      example: 'id:[20 TO 30]',
                    },
                    example: this.#queryParamExample({
                      filter: 'id:[20 TO 30]',
                    }),
                  },
                  {
                    name: 'page',
                    in: 'query',
                    description: 'The page number to return',
                    required: false,
                    schema: {
                      type: 'integer',
                      minimum: 1,
                      example: 1,
                    },
                    example: this.#queryParamExample({
                      page: 1,
                    }),
                  },
                  {
                    name: 'perPage',
                    in: 'query',
                    description: 'The number of items to return per page',
                    required: false,
                    schema: {
                      type: 'integer',
                      minimum: 1,
                      maximum: 100,
                      example: 20,
                    },
                    example: this.#queryParamExample({
                      perPage: 20,
                    }),
                  },
                  {
                    name: 'fields',
                    in: 'query',
                    description: this.#descriptionOfFieldWithPossibleValues(
                      'A comma separated list of the fields to include in the response',
                      'Available fields',
                      relatedPropertyNames
                    ),
                    required: false,
                    allowEmptyValue: true,
                    style: 'form',
                    explode: false,
                    schema: {
                      type: 'array',
                      items: {
                        type: 'string',
                        enum: relatedPropertyNames,
                      },
                    },
                    example: this.#queryParamExample({
                      fields:
                        relatedPropertyNames.length > 0
                          ? relatedPropertyNames.slice(1, 3).join(',')
                          : 'id',
                    }),
                  },
                  {
                    name: 'sort',
                    in: 'query',
                    description:
                      'The sort order to return results in. Sort operations will be applied in the order that they are specified.',
                    required: false,
                    allowEmptyValue: true,
                    explode: true,
                    style: 'deepObject',
                    schema: {
                      type: 'object',
                      properties: Object.assign(
                        {},
                        ...relatedSortable.map((field) => ({
                          [field]: {
                            type: 'string',
                            enum: ['asc', 'desc'],
                            default: 'asc',
                            example: 'asc',
                          },
                        }))
                      ),
                    },
                    example: this.#stringifyQueryObject({
                      sort:
                        relatedSortable.length > 0
                          ? Object.assign(
                              {},
                              ...relatedSortable.slice(0, 2).map((field) => ({ [field]: 'desc' }))
                            )
                          : { id: 'desc' },
                    }),
                  },
                  relatedAggregatable.length > 0
                    ? {
                        name: 'aggregations',
                        in: 'query',
                        description: this.#descriptionOfFieldWithPossibleValues(
                          'An object containing a comma separated list of the aggregations to return with the results, per field',
                          'Aggregatable fields',
                          relatedAggregatable
                        ),
                        required: false,
                        allowEmptyValue: true,
                        explode: true,
                        style: 'deepObject',
                        schema: {
                          type: 'object',
                          properties: Object.assign(
                            {},
                            ...relatedAggregatable.map((field) => ({
                              [field]: {
                                type: 'array',
                                items: {
                                  type: 'string',
                                  enum: ResourcefulAggregateMethods,
                                  description: this.#descriptionOfFieldWithPossibleValues(
                                    `The aggregation methods to apply to the ${field} field`,
                                    'Aggregation methods',
                                    Array.from(ResourcefulAggregateMethods)
                                  ),
                                },
                              },
                            }))
                          ),
                        },
                        example: this.#stringifyQueryObject({
                          aggregations: Object.assign(
                            {},
                            ...relatedAggregatable.slice(0, 2).map((field, index) => ({
                              [field]: ResourcefulAggregateMethods.slice(0, index + 1).join(','),
                            }))
                          ),
                        }),
                      }
                    : undefined,
                ].filter(Boolean) as OpenAPIV3.ParameterObject[],
                responses: {
                  '200': {
                    description: `A paginated list of related ${relatedResourcefulModelName} Model instances`,
                    headers: this.#getHeadersForResponseObject(
                      this.#opts.headers,
                      modelController.options.headers,
                      {
                        'x-metadata-url': this.#makeAbsoluteUrl(
                          ctx,
                          `${baseWithRelationship}/$meta.index`
                        ),
                        'x-shortcut-url': this.#makeAbsoluteUrl(
                          ctx,
                          [
                            baseWithRelationship,
                            's',
                            minify(
                              stripUndefinedValuesFromObject({
                                filter: 'id:[20 TO 30]',
                                page: 1,
                                perPage: 20,
                                fields:
                                  relatedPropertyNames.length > 0
                                    ? relatedPropertyNames.slice(1, 3).join(',')
                                    : 'id',
                                sort:
                                  relatedSortable.length > 0
                                    ? Object.assign(
                                        {},
                                        ...relatedSortable
                                          .slice(0, 2)
                                          .map((field) => ({ [field]: 'desc' }))
                                      )
                                    : { id: 'desc' },
                                aggregations: relatedAggregatable.length
                                  ? Object.assign(
                                      {},
                                      ...relatedAggregatable.slice(0, 2).map((field, index) => ({
                                        [field]: ResourcefulAggregateMethods.slice(
                                          0,
                                          index + 1
                                        ).join(','),
                                      }))
                                    )
                                  : undefined,
                              })
                            ),
                          ].join('/')
                        ),
                      }
                    ),
                    content: {
                      'application/json': indexResponseFor('json', relatedReadable.properties),
                      'application/x-yaml': indexResponseFor('yaml', relatedReadable.properties),
                    },
                  },
                },
              },
              put: {
                summary: `Sync the related ${name} ${relatedResourcefulModelName} Model instances for a specific ${resourcefulModelName} Model instance by its ID`,
                tags: [`${resourcefulModelName} Model`],
                operationId: string.camelCase(
                  ['serve', resourcefulModelName, 'update', 'related', name, 'put'].join('_')
                ),
                parameters: [
                  {
                    name: 'uid',
                    in: 'path',
                    description: `The unique identifier of the ${resourcefulModelName} Model instance to retrieve related instances for`,
                    required: true,
                    schema: {
                      type: 'string',
                      example: '1',
                    },
                  },
                ],
                requestBody: {
                  $ref: '#/components/requestBodies/SyncRelatedInstancesRequestBody',
                },
                responses: {
                  204: {
                    description: `The related ${relatedResourcefulModelName} Model instances were successfully synced`,
                    headers: this.#getHeadersForResponseObject(
                      this.#opts.headers,
                      modelController.options.headers
                    ),
                  },
                },
              },
              patch: {
                summary: `Sync the related ${name} ${relatedResourcefulModelName} Model instances for a specific ${resourcefulModelName} Model instance by its ID`,
                tags: [`${resourcefulModelName} Model`],
                operationId: string.camelCase(
                  ['serve', resourcefulModelName, 'update', 'related', name, 'patch'].join('_')
                ),
                parameters: [
                  {
                    name: 'uid',
                    in: 'path',
                    description: `The unique identifier of the ${resourcefulModelName} Model instance to retrieve related instances for`,
                    required: true,
                    schema: {
                      type: 'string',
                      example: '1',
                    },
                  },
                ],
                requestBody: {
                  $ref: '#/components/requestBodies/SyncRelatedInstancesRequestBody',
                },
                responses: {
                  204: {
                    description: `The related ${relatedResourcefulModelName} Model instances were successfully synced`,
                    headers: this.#getHeadersForResponseObject(
                      this.#opts.headers,
                      modelController.options.headers
                    ),
                  },
                },
              },
            }
            ret[`${baseWithRelationship}/s/{encoded}`] = {
              get: {
                summary: `Shortcut to Retrieve the related ${name} ${relatedResourcefulModelName} Model instances for a specific ${resourcefulModelName} Model instance by its ID`,
                tags: [`${resourcefulModelName} Model`],
                operationId: string.camelCase(
                  ['serve', resourcefulModelName, 'read', 'related', name, 'shortcut'].join('_')
                ),
                parameters: [
                  {
                    name: 'encoded',
                    in: 'path',
                    description: `A URL encoded string representing the filter, page, perPage, fields, sort and aggregations parameters, generated via the \`encode\` method provided by the \`@nhtio/lucid-resourceful/encoding\` package`,
                    required: true,
                    schema: {
                      type: 'string',
                      example: minify(
                        stripUndefinedValuesFromObject({
                          filter: 'id:[20 TO 30]',
                          page: 1,
                          perPage: 20,
                          fields:
                            relatedPropertyNames.length > 0
                              ? relatedPropertyNames.slice(1, 3).join(',')
                              : 'id',
                          sort:
                            relatedSortable.length > 0
                              ? Object.assign(
                                  {},
                                  ...relatedSortable
                                    .slice(0, 2)
                                    .map((field) => ({ [field]: 'desc' }))
                                )
                              : { id: 'desc' },
                          aggregations: relatedAggregatable.length
                            ? Object.assign(
                                {},
                                ...relatedAggregatable.slice(0, 2).map((field, index) => ({
                                  [field]: ResourcefulAggregateMethods.slice(0, index + 1).join(
                                    ','
                                  ),
                                }))
                              )
                            : undefined,
                        })
                      ),
                    },
                  },
                ],
                responses: {
                  '200': {
                    description: 'A paginated list of model instances matching the requested query',
                    headers: this.#getHeadersForResponseObject(
                      this.#opts.headers,
                      modelController.options.headers,
                      {
                        'x-metadata-url': this.#makeAbsoluteUrl(
                          ctx,
                          `${baseWithRelationship}/$meta.index`
                        ),
                        'x-full-url': this.#makeAbsoluteUrl(
                          ctx,
                          [
                            baseWithRelationship,
                            's',
                            qs.stringify(
                              stripUndefinedValuesFromObject({
                                filter: 'id:[20 TO 30]',
                                page: 1,
                                perPage: 20,
                                fields:
                                  relatedPropertyNames.length > 0
                                    ? relatedPropertyNames.slice(1, 3).join(',')
                                    : 'id',
                                sort:
                                  relatedSortable.length > 0
                                    ? Object.assign(
                                        {},
                                        ...relatedSortable
                                          .slice(0, 2)
                                          .map((field) => ({ [field]: 'desc' }))
                                      )
                                    : { id: 'desc' },
                                aggregations: relatedAggregatable.length
                                  ? Object.assign(
                                      {},
                                      ...relatedAggregatable.slice(0, 2).map((field, index) => ({
                                        [field]: ResourcefulAggregateMethods.slice(
                                          0,
                                          index + 1
                                        ).join(','),
                                      }))
                                    )
                                  : undefined,
                              }),
                              { addQueryPrefix: true }
                            ),
                          ].join('/')
                        ),
                      }
                    ),
                    content: {
                      'application/json': indexResponseFor('json', relatedReadable.properties),
                      'application/x-yaml': indexResponseFor('yaml', relatedReadable.properties),
                    },
                  },
                },
              },
            }
            ret[`${baseWithRelationship}/$meta.index`] = {
              get: {
                summary: `Related ${relatedResourcefulModelName} index operation metadata`,
                tags: [`${resourcefulModelName} Model`],
                operationId: string.camelCase(
                  ['serve', resourcefulModelName, 'read', 'related', name, 'metadata'].join('_')
                ),
                responses: {
                  '200': {
                    description: `The metadata for the ${resourcefulModelName} index operation`,
                    headers: this.#getHeadersForResponseObject(
                      this.#opts.headers,
                      modelController.options.headers
                    ),
                    content: {
                      'application/json': this.#makeMetaIndexResponseSchema(
                        relatedReadable.properties as any,
                        relatedFilterable,
                        relatedSortable,
                        relatedAggregatable
                      ),
                      'application/x-yaml': this.#makeMetaIndexResponseSchema(
                        relatedReadable.properties as any,
                        relatedFilterable,
                        relatedSortable,
                        relatedAggregatable,
                        'yaml'
                      ),
                    },
                  },
                },
              },
            }
          },
          { concurrency: 1 }
        )
        this.#opts.security.forEach((def) => {
          const applyToModel = isObject(def.applyToModels)
            ? def.applyToModels[prefix]
            : def.applyToModels
          if (applyToModel === false || !applyToModel) return
          const toApply: Map<string, any[]> = new Map()
          if (applyToModel === true) {
            toApply.set([base, 'get'].join('|'), [])
            toApply.set([base, 'post'].join('|'), [])
            toApply.set([base, 'put'].join('|'), [])
            toApply.set([base, 'patch'].join('|'), [])
            toApply.set([`${base}/{uid}`, 'get'].join('|'), [])
            toApply.set([`${base}/{uid}`, 'put'].join('|'), [])
            toApply.set([`${base}/{uid}`, 'patch'].join('|'), [])
            toApply.set([`${base}/{uid}`, 'delete'].join('|'), [])
            toApply.set([`${base}/$bulk`, 'put'].join('|'), [])
            toApply.set([`${base}/$bulk`, 'patch'].join('|'), [])
            toApply.set([`${base}/$bulk/{uids}`, 'put'].join('|'), [])
            toApply.set([`${base}/$bulk/{uids}`, 'patch'].join('|'), [])
            toApply.set([`${base}/s/{encoded}`, 'get'].join('|'), [])
            relationshipPaths.forEach((path) => {
              toApply.set([path, 'get'].join('|'), [])
              toApply.set([path, 'put'].join('|'), [])
              toApply.set([path, 'patch'].join('|'), [])
            })
          } else if (isObject(applyToModel)) {
            Object.entries(applyToModel).forEach(([resourcefulRouterVerb, requiredPermissions]) => {
              if (!requiredPermissions) return
              const permissionsToApply = true === requiredPermissions ? [] : requiredPermissions
              switch (resourcefulRouterVerb) {
                case 'index':
                  toApply.set([base, 'get'].join('|'), permissionsToApply)
                  toApply.set([`${base}/s/{encoded}`, 'get'].join('|'), permissionsToApply)
                  break
                case 'create':
                  toApply.set([base, 'post'].join('|'), permissionsToApply)
                  break
                case 'read':
                  toApply.set([`${base}/{uid}`, 'get'].join('|'), permissionsToApply)
                  break
                case 'readRelated':
                  relationshipPaths.forEach((path) => {
                    toApply.set([path, 'get'].join('|'), permissionsToApply)
                    toApply.set([`${path}/s/{encoded}`, 'get'].join('|'), permissionsToApply)
                  })
                  break
                case 'update':
                  toApply.set([base, 'put'].join('|'), permissionsToApply)
                  toApply.set([base, 'patch'].join('|'), permissionsToApply)
                  toApply.set([`${base}/{uid}`, 'put'].join('|'), permissionsToApply)
                  toApply.set([`${base}/{uid}`, 'patch'].join('|'), permissionsToApply)
                  break
                case 'bulkUpdate':
                  toApply.set([`${base}/$bulk`, 'put'].join('|'), permissionsToApply)
                  toApply.set([`${base}/$bulk`, 'patch'].join('|'), permissionsToApply)
                  toApply.set([`${base}/$bulk/{uids}`, 'put'].join('|'), permissionsToApply)
                  toApply.set([`${base}/$bulk/{uids}`, 'patch'].join('|'), permissionsToApply)
                  break
                case 'syncRelated':
                  relationshipPaths.forEach((path) => {
                    toApply.set([path, 'put'].join('|'), permissionsToApply)
                    toApply.set([path, 'patch'].join('|'), permissionsToApply)
                  })
                  break
                case 'delete':
                  toApply.set([`${base}/{uid}`, 'delete'].join('|'), permissionsToApply)
                  break
              }
            })
          }
          toApply.forEach((permissions, relativePathAndMethod) => {
            const [relativePath, method] = relativePathAndMethod.split('|') as [
              keyof typeof ret,
              ResourcefulHttpVerb,
            ]
            if (
              typeof ret[relativePath] === 'undefined' ||
              !isObject(ret[relativePath]) ||
              typeof ret[relativePath][method] === 'undefined' ||
              !isObject(ret[relativePath][method])
            )
              return false
            if (!Array.isArray(ret[relativePath][method].security)) {
              ret[relativePath][method].security = []
            }
            ret[relativePath][method].security.push({
              [def.key]: permissions,
            })
          })
        })
        ResourcefulRouterVerb.forEach((verb) => {
          if (this.#opts.except.includes(verb) || modelController.options.except.includes(verb)) {
            switch (verb) {
              case 'index':
                delete ret[`${base}/$meta.index`]
                delete ret[`${base}/s/{encoded}`]
                delete ret[base]?.get
                break
              case 'create':
                delete ret[`${base}/$meta.create`]
                delete ret[base]?.post
                break
              case 'read':
                delete ret[`${base}/{uid}`]?.get
                delete ret[`${base}/$meta.read`]
                break
              case 'readRelated':
                Object.entries(ret).forEach(([key]) => {
                  if (key.startsWith(`${base}/{uid}/`)) {
                    delete ret[key]?.get
                  }
                })
                break
              case 'update':
                delete ret[base]?.put
                delete ret[base]?.patch
                delete ret[`${base}/{uid}`]?.put
                delete ret[`${base}/{uid}`]?.patch
                delete ret[`${base}/$meta.update`]
                break
              case 'bulkUpdate':
                delete ret[`${base}/$bulk`]?.put
                delete ret[`${base}/$bulk`]?.patch
                delete ret[`${base}/$bulk/{uids}`]?.put
                delete ret[`${base}/$bulk/{uids}`]?.patch
                delete ret[`${base}/$meta.bulk.update`]
                break
              case 'syncRelated':
                Object.entries(ret).forEach(([key]) => {
                  if (key.startsWith(`${base}/{uid}/`)) {
                    delete ret[key]?.put
                    delete ret[key]?.patch
                  }
                })
                break
              case 'delete':
                delete ret[`${base}/{uid}`]?.delete
                break
            }
          }
        })
        Object.keys(ret).forEach((key) => {
          if (isObject(ret[key]) && Object.keys(ret[key]).length === 0) {
            delete ret[key]
          }
        })
        Object.entries(modelController.options.additional).forEach(
          ([methodAndRelativePath, callbackOrRouteDetails]) => {
            const [method, relativePath] = methodAndRelativePath.split('|')
            if (!ResourcefulHttpVerb.includes(method.toLowerCase() as any)) return
            const resourcefulHttpMethod = method.toLowerCase() as ResourcefulHttpVerb
            const pathKey = `${base}/${relativePath}`.replace('//', '/')
            const schema = this.#makeSchemaForAdditionalRoute(
              resourcefulHttpMethod,
              pathKey,
              callbackOrRouteDetails
            )
            const matchingSecuritySchemaDefinitions = this.#opts.security
              .filter(
                (def) =>
                  def.applyToAdditional === true ||
                  Object.keys(def.applyToAdditional).includes(pathKey)
              )
              .map((def) => ({
                key: def.key,
                application: isObject(def.applyToAdditional)
                  ? def.applyToAdditional[pathKey as keyof typeof def.applyToAdditional]!
                  : def.applyToAdditional,
              }))
              .filter((def) => def.application !== false)
            if (matchingSecuritySchemaDefinitions.length > 0) {
              if (!schema.security) {
                schema.security = []
              }
              matchingSecuritySchemaDefinitions.forEach((def) => {
                if (def.application === false) return
                schema.security!.push({
                  [def.key]: def.application === true ? [] : def.application,
                })
              })
            }
            if (!ret[pathKey]) ret[pathKey] = {}
            if (!ret[pathKey][resourcefulHttpMethod]) ret[pathKey][resourcefulHttpMethod] = schema
          }
        )
      },
      { concurrency: 10 }
    )
    Object.entries(this.#opts.additional).forEach(
      ([methodAndRelativePath, callbackOrRouteDetails]) => {
        const [method, relativePath] = methodAndRelativePath.split('|')
        if (!ResourcefulHttpVerb.includes(method.toLowerCase() as any)) return
        const resourcefulHttpMethod = method.toLowerCase() as ResourcefulHttpVerb
        const pathKey = relativePath
        const schema = this.#makeSchemaForAdditionalRoute(
          resourcefulHttpMethod,
          pathKey,
          callbackOrRouteDetails
        )
        const matchingSecuritySchemaDefinitions = this.#opts.security
          .filter(
            (def) =>
              def.applyToAdditional === true ||
              Object.keys(def.applyToAdditional).includes(methodAndRelativePath)
          )
          .map((def) => ({
            key: def.key,
            application: isObject(def.applyToAdditional)
              ? def.applyToAdditional[pathKey as keyof typeof def.applyToAdditional]!
              : def.applyToAdditional,
          }))
          .filter((def) => def.application !== false)
        if (matchingSecuritySchemaDefinitions.length > 0) {
          if (!schema.security) {
            schema.security = []
          }
          matchingSecuritySchemaDefinitions.forEach((def) => {
            if (def.application === false) return
            schema.security!.push({
              [def.key]: def.application === true ? [] : def.application,
            })
          })
        }
        if (!ret[pathKey]) ret[pathKey] = {}
        if (!ret[pathKey][resourcefulHttpMethod]) ret[pathKey][resourcefulHttpMethod] = schema
      }
    )
    const errorResponseReferences = getPossibleHttpErrorCodes().map((c) => ({
      [c.toString()]: {
        $ref: `#/components/responses/${string.camelCase(['error', c].join('_'))}`,
      },
    }))
    Object.entries(ret).forEach(([path, operations]) => {
      Object.entries(operations!).forEach(([method, operation]) => {
        if (isObject(operation)) {
          if (this.#opts.catchThrown) {
            if (!operation.responses) {
              operation.responses = {}
            }
            operation.responses = deepmerge(
              operation.responses,
              ...errorResponseReferences
            ) as OpenAPIV3.ResponsesObject
            // @ts-ignore
            ret[path][method] = operation
          }
        }
      })
    })
    return Object.assign(
      {},
      ...Object.entries(ret)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([k, v]) => ({
          [k]: Object.assign(
            {},
            ...Object.entries(v!)
              .sort(([a], [b]) => {
                const aOrder = HttpMethodSortOrder[a as keyof typeof HttpMethodSortOrder]
                const bOrder = HttpMethodSortOrder[b as keyof typeof HttpMethodSortOrder]
                if (!aOrder || !bOrder) return a.localeCompare(b)
                return aOrder - bOrder
              })
              .map(([k2, v2]) => ({ [k2]: v2 }))
          ),
        }))
    )
  }

  async #components(): Promise<OpenAPIV3.Document['components']> {
    const syncRelatedInstancesSchema = {
      type: 'array' as OpenAPIV3.ArraySchemaObjectType,
      items: {
        description: 'An object representing a related instance to sync, including pivot data',
        type: 'object' as OpenAPIV3.NonArraySchemaObjectType,
        properties: {
          ruid: {
            type: 'string' as OpenAPIV3.NonArraySchemaObjectType,
            description: 'The unique identifier of the related instance',
            example: '1',
          },
        },
        additionalProperties: true,
        required: ['ruid'],
      },
      uniqueItems: true,
    }

    const ret: OpenAPIV3.ComponentsObject = {
      schemas: {},
      requestBodies: {
        SyncRelatedInstancesRequestBody: {
          description:
            'A request body to sync related instances.\n\n**Note**: For Many to Many relationships, additional properties may be specified to update pivot properties.',
          content: {
            'application/json': {
              schema: syncRelatedInstancesSchema,
              example: JSON.stringify(
                [{ ruid: '1' }, { ruid: '2', pivotValue: 'foo' }, { ruid: '3' }],
                null,
                2
              ),
            },
            'application/x-www-form-urlencoded': {
              schema: syncRelatedInstancesSchema,
              example: qs.stringify([
                { ruid: '1' },
                { ruid: '2', pivotValue: 'foo' },
                { ruid: '3' },
              ]),
            },
            'multipart/form-data': {
              schema: syncRelatedInstancesSchema,
              example: [{ ruid: '1' }, { ruid: '2', pivotValue: 'foo' }, { ruid: '3' }],
            },
            'application/x-yaml': {
              schema: syncRelatedInstancesSchema,
              example: YAML.stringify([
                { ruid: '1' },
                { ruid: '2', pivotValue: 'foo' },
                { ruid: '3' },
              ]),
            },
          },
        },
      },
    }
    if (this.#opts.catchThrown) {
      if (!ret.responses) {
        ret.responses = {}
      }
      const errorResponses: { [key: string]: OpenAPIV3.ResponseObject } = {}
      const errorCodes = getPossibleHttpErrorCodes()
      errorCodes.forEach((code) => {
        const key = string.camelCase(['error', code].join('_'))
        const errorCodeSchema: OpenAPIV3.MediaTypeObject = {
          schema: {
            type: 'object',
            required: ['status', 'code', 'message'],
            properties: {
              status: {
                type: 'number',
                enum: [code],
                example: code,
                description: 'The HTTP Status code of the error',
              },
              code: {
                type: 'string',
                example: 'E_SOME_ERROR_CODE',
                description:
                  'A machine readable code used to differentiate different errors within the same HTTP status code',
              },
              message: {
                type: 'string',
                example: 'Some message which is much friendier to humans',
                description: 'A message meant to be consumed by non-machine consumers',
              },
              help: {
                type: 'string',
                example: 'To fix this error, change something',
                description: 'Optional help text providing guidance on how to resolve the error',
              },
              details: {
                description: 'Array of detailed error information, typically for validation errors',
                type: 'array',
                items: {
                  description:
                    'Provides field-level information for validation errors or error chain details.',
                  type: 'object',
                  required: ['code', 'message'],
                  properties: {
                    code: {
                      type: 'string',
                      example: 'E_SOME_ERROR_CODE',
                      description:
                        'A machine readable code used to differentiate different errors within the same HTTP status code',
                    },
                    message: {
                      type: 'string',
                      example: 'Some message which is much friendier to humans',
                      description: 'A message meant to be consumed by non-machine consumers',
                    },
                    target: {
                      type: 'string',
                      example: 'yourObject.yourObjectField',
                      description:
                        'The field or property that caused the error (for validation errors)',
                    },
                    context: {
                      type: 'object',
                      example: {},
                      description: 'Additional context information about the error',
                      additionalProperties: true,
                    },
                  },
                },
              },
            },
          },
        }
        errorResponses[key] = {
          description: describeHttpError(code),
          headers: this.#getHeadersForResponseObject(this.#opts.headers),
          content: {
            'application/json': errorCodeSchema,
            'application/x-www-form-urlencoded': errorCodeSchema,
            'multipart/form-data': errorCodeSchema,
            'application/x-yaml': errorCodeSchema,
          },
        }
      })
      ret.responses = deepmerge(ret.responses, errorResponses)
    }
    const prefixesAndModels = Array.from(this.#models.entries())
    await pMap(
      prefixesAndModels,
      async ([_prefix, model]) => {
        if (!isResourcefulModel(model)) return
        const readable = this.#modelReadSchemas.get(model)
        const key = model.$resourcefulName
        if (readable && key) {
          ret.schemas = ret.schemas || {}
          ret.schemas[key] = readable
        }
      },
      {
        concurrency: 5,
      }
    )
    const securitySchemas: { [key: string]: OpenAPIV3.SecuritySchemeObject } = {}
    this.#opts.security.forEach((def) => {
      securitySchemas[def.key] = def.schema
    })
    ret.securitySchemes = securitySchemas
    return ret
  }

  async #security(): Promise<OpenAPIV3.Document['security']> {
    return undefined
  }

  async #tags(): Promise<OpenAPIV3.Document['tags']> {
    return undefined
  }

  async #externalDocs(): Promise<OpenAPIV3.Document['externalDocs']> {
    return this.#opts.externalDocs || undefined
  }

  async #getJsonAndYamlCompatibleObject(ctx: HttpContext): Promise<OpenAPIV3.Document> {
    const [openapi, info, servers, paths, components, security, tags, externalDocs] =
      await Promise.all([
        this.#openapi(),
        this.#info(),
        this.#servers(ctx),
        this.#paths(ctx),
        this.#components(),
        this.#security(),
        this.#tags(),
        this.#externalDocs(),
      ])
    return {
      openapi,
      info,
      servers,
      paths,
      components,
      security,
      tags,
      externalDocs,
    }
  }

  async #serveAsJson(ctx: HttpContext): Promise<void> {
    const spec = await this.#getJsonAndYamlCompatibleObject(ctx)
    return ctx.response
      .header('Content-Type', 'application/json')
      .status(200)
      .send(JSON.stringify(spec, encoder))
  }

  async #serveAsYaml(ctx: HttpContext): Promise<void> {
    const spec = await this.#getJsonAndYamlCompatibleObject(ctx)
    return ctx.response
      .header('Content-Type', 'application/x-yaml')
      .status(200)
      .send(YAML.stringify(spec, encoder))
  }

  async serveAs(format: ResourcefulResponseFormat, ctx: HttpContext): Promise<void> {
    switch (format) {
      case 'yaml':
        return await this.#serveAsYaml(ctx)
      default:
        return await this.#serveAsJson(ctx)
    }
  }
}
